import { FC } from "react";
import { Link, generatePath } from "react-router-dom";
import { STAFF_USER_ROUTE } from "../../../../app/router/routes";
import { IAuditStudentWalletHistoryItem } from "../../model/types";
import { formatDateTime } from "../../../../widgets/scheduleCalendar/lib/scheduleDate";
import classes from "./walletHistoryList.module.scss";

interface IProps {
    items: IAuditStudentWalletHistoryItem[];
    typeLabels: Record<string, string>;
    emptyText: string;
}

const trimTrailingZeros = (fraction: string): string => {
    const normalized = fraction.replace(/0+$/, '')
    return normalized ? `,${normalized}` : ''
}

const formatMoney = (value: string): string => {
    const normalized = value.trim().replace(/\s/g, '').replace(',', '.')

    if(!normalized) return '0 ₽'

    const sign = normalized.startsWith('-') ? '-' : normalized.startsWith('+') ? '+' : ''
    const unsigned = sign ? normalized.slice(1) : normalized
    const [integerRaw, fractionRaw = ''] = unsigned.split('.')
    const integer = integerRaw || '0'
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    const fraction = fractionRaw ? trimTrailingZeros(fractionRaw) : ''

    return `${sign}${formattedInteger}${fraction} ₽`
}

const getDeltaClassName = (value: string): string => {
    const normalized = value.trim().replace(/\s/g, '').replace(',', '.')

    if(normalized.startsWith('-') && !/^-?0(?:\.0+)?$/.test(normalized)){
        return classes.negative
    }

    if(!normalized.startsWith('-') && !/^\+?0(?:\.0+)?$/.test(normalized)){
        return classes.positive
    }

    return classes.neutral
}

const getAuthorLabel = (item: IAuditStudentWalletHistoryItem): string => {
    if(item.type === 'WALLET_HISTORY_TYPE_TRANSACTION_CONFIRMED'){
        return 'Система'
    }

    const fullName = item.updated_by_full_name.trim()
    const roleName = item.updated_by_role_name.trim()

    if(fullName && roleName) return `${fullName} · ${roleName}`
    if(fullName) return fullName
    if(roleName) return roleName

    return 'Не указан'
}

const getAuthorUserId = (item: IAuditStudentWalletHistoryItem): number => {
    if(item.type === 'WALLET_HISTORY_TYPE_TRANSACTION_CONFIRMED'){
        return 0
    }

    const userId = item.audit?.updatedByUserId || item.audit?.createdByUserId || ''
    const userIdNumber = Number(userId)

    return Number.isFinite(userIdNumber) && userIdNumber > 0 ? userIdNumber : 0
}

export const WalletHistoryList: FC<IProps> = ({items, typeLabels, emptyText}) => {
    if(!items.length){
        return <section className={classes.empty}>{emptyText}</section>
    }

    return (
        <section className={classes.list}>
            {items.map((item, index) => {
                const changedAt = item.audit?.updatedAt || item.audit?.createdAt || ''
                const typeLabel = typeLabels[item.type] || item.action || item.type || 'Изменение кошелька'
                const authorLabel = getAuthorLabel(item)
                const authorUserId = getAuthorUserId(item)
                const canOpenAuthor = authorUserId > 0 && item.updated_by_full_name.trim()

                return (
                    <section
                        key={`${item.id}-${changedAt || index}`}
                        className={classes.item}
                    >
                        <section className={classes.top}>
                            <section className={classes.title}>
                                {changedAt ? formatDateTime(changedAt) : 'Дата не указана'}
                            </section>
                            <span className={classes.type}>{typeLabel}</span>
                        </section>

                        <section className={classes.grid}>
                            <section className={classes.field}>
                                <section className={classes.label}>Баланс</section>
                                <section className={classes.value}>
                                    {formatMoney(item.balance_before)} → {formatMoney(item.balance_after)}
                                </section>
                            </section>

                            <section className={classes.field}>
                                <section className={classes.label}>Изменение</section>
                                <section className={`${classes.value} ${classes.delta} ${getDeltaClassName(item.balance_delta)}`}>
                                    {formatMoney(item.balance_delta)}
                                </section>
                            </section>

                            <section className={classes.field}>
                                <section className={classes.label}>Автор</section>
                                <section className={classes.value}>
                                    {canOpenAuthor
                                        ? <Link className={classes.link} to={generatePath(STAFF_USER_ROUTE.path, {user_id: String(authorUserId)})}>
                                            {authorLabel}
                                        </Link>
                                        : authorLabel}
                                </section>
                            </section>
                        </section>

                    </section>
                )
            })}
        </section>
    )
}
